import {
  Alert,
  AlertActionCloseButton,
  Divider,
  Flex,
  FlexItem,
  PageSection,
  PageSectionVariants,
  Tab,
  Tabs,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { PatientCard, VisitSummaryCard } from '@app/components'
import { ResponseHistoryTable } from '@app/components/patient/response-history/response-history-list'
import { InstrumentReports } from '@app/components/visit/reports/instrument-reports'
import { VisitReports } from '@app/components/visit/reports/visit-reports'
import { useEntry } from '@app/domain/entry/entry.hooks'
import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import type { IEntry } from '@app/domain/instrument/instrument.interface'
import { CommunicationConsentSummaryCard } from '@app/domain/interaction/components/communcation-consent-summary-card.component'
import {
  useGetInteractionPdf,
  useInteraction,
} from '@app/domain/interaction/interaction.hooks'
import { usePatient, useUpdatePatient } from '@app/domain/patient/patient.hooks'
import { getVisitInstrumentPdfs } from '@app/domain/visit/visit.helpers'
import { useVisit } from '@app/domain/visit/visit.hooks'
import type { IDataInstrument } from '@app/domain/visit/visit.interface'
import type { RootState } from '@app/state'
import type { State as ConfigState } from '@app/state/config'
import type {
  IApiUpdatePatient} from '@app/state/patients';
import {
  PatientActionStatusId,
  actions as PatientActions,
} from '@app/state/patients'
import type { IApiUpdateVisit} from '@app/state/visits';
import { actions as VisitActions } from '@app/state/visits'
import * as Visits from '@app/state/visits'
import type { ActionStatusId } from '@app/state/visits/state'

interface RootParams {
  id: string
}

interface VisitHeaderComponentProps {
  visitId: string
}

export const VisitHeaderComponent = ({
  visitId,
}: VisitHeaderComponentProps) => {
  const dispatch = useDispatch()
  const config = useSelector<RootState, ConfigState>((state) => state.config)

  const { visit } = useVisit(visitId)

  const updateVisitStatus = useSelector<RootState, ActionStatusId>(
    (state) => state.visits.visitAction?.status,
  )

  const updateVisit = useCallback(
    (id: string, updatedVisit: IApiUpdateVisit) => {
      dispatch(VisitActions.updateVisit(id, updatedVisit))
    },
    [dispatch],
  )

  const clearVisitActionStatus = useCallback(() => {
    dispatch(VisitActions.clearActionStatus())
  }, [dispatch])

  if (config.clinic && visit) {
    return (
      <VisitSummaryCard
        visit={visit}
        visitStatuses={Object.values(config.clinic.statuses)}
        updateVisit={updateVisit}
        updateStatus={updateVisitStatus}
        clearStatus={clearVisitActionStatus}
      />
    )
  } else {
    return <></>
  }
}

interface PatientHeaderComponentProps {
  patientId: string
}

export const PatientHeaderComponent = ({
  patientId,
}: PatientHeaderComponentProps) => {
  const dispatch = useDispatch()

  const [updatePatientStatus, setUpdatePatientStatus] =
    useState<PatientActionStatusId>()

  const { getPatient, patient } = usePatient(patientId)
  const { updatePatient } = useUpdatePatient()

  const clearPatientActionStatus = useCallback(() => {
    setUpdatePatientStatus(null)
    dispatch(PatientActions.clearActionStatus())
  }, [dispatch])

  const handleUpdatePatient = useCallback(
    (id: string, updatedPatient: IApiUpdatePatient) => {
      updatePatient(id, updatedPatient)
        .then(() => {
          getPatient()
          setUpdatePatientStatus(PatientActionStatusId.Succeeded)
        })
        .catch(() => {
          setUpdatePatientStatus(PatientActionStatusId.Failed)
        })
    },
    [getPatient, updatePatient],
  )

  if (patient) {
    return (
      <PatientCard
        isSelectPatientAllowed={false}
        onClearStatus={clearPatientActionStatus}
        onUpdatePatient={handleUpdatePatient}
        patient={patient}
        updateStatus={updatePatientStatus}
      />
    )
  } else {
    return <></>
  }
}

export interface EntryFormProps {
  entryId: string
}

export const EntryForm = ({ entryId }: EntryFormProps) => {
  const { entry, updateResponses } = useEntry(entryId)

  const handleEditResponses = useCallback(
    (_entryId: string, responses: Record<string, string>) => {
      updateResponses(responses)
    },
    [updateResponses],
  )

  return entry ? (
    <ResponseHistoryTable
      entries={[entry]}
      hideHeader={true}
      instrumentId={entry.instrumentId}
      instrumentVersion={entry.instrumentVersion}
      onEditResponses={handleEditResponses}
    />
  ) : null
}

interface InstrumentsTabProps {
  instruments: IDataInstrument[]
  entries: IEntry[]
}

const InstrumentsTab = ({ entries, instruments }: InstrumentsTabProps) => {
  const tabs = useMemo(() => {
    return instruments.map((i, index) => {
      const matchingEntries = entries.filter(
        (entry) => entry.instrumentId === i.id,
      )

      return (
        <Tab key={index} eventKey={index} title={displayLabelIfExists(i)}>
          {i.questions !== undefined && Object.keys(i.questions).length > 0 ? (
            <ResponseHistoryTable
              entries={matchingEntries}
              hideHeader={true}
              instrumentId={i.id}
              instrumentVersion={i.version}
            />
          ) : (
            'This instrument does not have any questions configured.'
          )}
        </Tab>
      )
    })
  }, [entries, instruments])

  const [activeTabKey, setActiveTabKey] = useState(0)

  const handleTabClick = useCallback((event, tabIndex) => {
    setActiveTabKey(tabIndex)
  }, [])

  return (
    <Tabs
      activeKey={activeTabKey}
      onSelect={handleTabClick}
      addButtonAriaLabel="Add Instrument"
      isSecondary={true}
    >
      {tabs}
    </Tabs>
  )
}

enum TabKeys {
  Instruments,
  Reports,
}

export const InteractionScreen = () => {
  const config = useSelector<RootState, ConfigState>((state) => state.config)
  const dispatch = useDispatch()

  const {
    getInteractionPdf,
    interactionPdfError,
    clearInteractionPdfError,
    isInteractionPdfLoading,
  } = useGetInteractionPdf()

  const downloadReportErrorMessage = useSelector<RootState, string>(
    (state) => state.visits.downloadReportErrorMessage,
  )

  const isInstrumentPdfLoading = useSelector<RootState, boolean>(
    (state) => state.visits.isInstrumentPdfLoading,
  )

  const { id } = useParams<RootParams>()
  const {
    patientId,
    visitId,
    entries,
    instruments,
    communicationConsent,
    reportTemplates,
    status: interactionStatus,
  } = useInteraction(id)

  const [activeTabKey, setActiveTabKey] = useState<number>(TabKeys.Instruments)

  const setActiveTab = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab)
  }, [])

  const handleGetVisitPdf = useCallback(
    (templateId: string) => {
      getInteractionPdf(id, templateId)
    },
    [id, getInteractionPdf],
  )

  const instrumentReports = React.useMemo(() => {
    const entryMap = entries.reduce((acc, entry) => {
      acc[entry.instrumentId] = entry
      return acc
    }, {})
    return getVisitInstrumentPdfs(instruments, entryMap)
  }, [instruments, entries])

  const downloadEntryPDF = useCallback(
    (entryId: string, templateId: string) => {
      dispatch(Visits.actions.downloadEntryPDF(entryId, templateId))
    },
    [dispatch],
  )

  const handleCloseError = useCallback(() => {
    if (interactionPdfError) {
      clearInteractionPdfError()
    } else if (downloadReportErrorMessage) {
      dispatch(Visits.actions.clearDownloadError())
    }
  }, [
    interactionPdfError,
    downloadReportErrorMessage,
    clearInteractionPdfError,
    dispatch,
  ])

  if (interactionStatus.overall === 'success' && config.clinic) {
    return (
      <>
        <PageSection>
          <Flex spaceItems={{ default: 'spaceItems2xl' }}>
            <FlexItem>
              <VisitHeaderComponent visitId={visitId} />
            </FlexItem>
            {visitId && patientId ? (
              <Divider isVertical={true} inset={{ default: 'insetMd' }} />
            ) : null}
            <FlexItem>
              <PatientHeaderComponent patientId={patientId} />
            </FlexItem>
            {communicationConsent ? (
              <>
                <Divider isVertical={true} inset={{ default: 'insetMd' }} />
                <CommunicationConsentSummaryCard
                  communicationConsent={communicationConsent}
                />
              </>
            ) : null}
          </Flex>
        </PageSection>
        <PageSection variant={PageSectionVariants.light}>
          <Tabs
            activeKey={activeTabKey}
            onSelect={setActiveTab}
            isBox={true}
            role="region"
          >
            <Tab eventKey={TabKeys.Instruments} title="Instruments">
              <PageSection isFilled={true}>
                <InstrumentsTab instruments={instruments} entries={entries} />
              </PageSection>
            </Tab>
            <Tab eventKey={TabKeys.Reports} title="Reports">
              <PageSection isFilled={true}>
                {(interactionPdfError || downloadReportErrorMessage) && (
                  <Alert
                    variant="danger"
                    title={interactionPdfError || downloadReportErrorMessage}
                    isInline
                    actionClose={
                      <AlertActionCloseButton onClose={handleCloseError} />
                    }
                  />
                )}
                <PageSection>
                  <VisitReports
                    isDownloading={isInteractionPdfLoading}
                    visitPdfs={reportTemplates}
                    getVisitPdf={handleGetVisitPdf}
                  />
                </PageSection>
                <PageSection>
                  <InstrumentReports
                    isDownloading={isInstrumentPdfLoading}
                    instrumentPdfs={instrumentReports}
                    getEntryPdf={downloadEntryPDF}
                  />
                </PageSection>
              </PageSection>
            </Tab>
          </Tabs>
        </PageSection>
      </>
    )
  } else if (interactionStatus.overall === 'loading') {
    return (
      <PageSection>
        <h1>Loading...</h1>
      </PageSection>
    )
  } else if (interactionStatus.overall === 'error') {
    return (
      <PageSection>
        <h1>No Interaction Found</h1>
      </PageSection>
    )
  } else {
    return null
  }
}
